import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  constructionSiteErrorMessage,
  extractConstructionSiteError,
  isEditNotAllowed,
} from '../functions/construction-site-error.function';
import {
  hasConstructionSiteChanges,
  toCreateRequest,
  toFormValue,
  toUpdateRequest,
} from '../functions/construction-site-request.function';
import { ConstructionSiteRepository } from '../repositories/construction-site.repository';
import { CustomerRepository } from '../repositories/customer.repository';
import { ConstructionSiteError } from '../types/construction-site';
import {
  ConstructionSiteFormValue,
  EMPTY_CONSTRUCTION_SITE_FORM_VALUE,
} from '../types/construction-site-form';

export type ConstructionSiteFormMode = 'create' | 'update';

/**
 * State of the construction-site form: prefill for an existing site, the edited
 * value, and the create/patch submission including the backend's error keys.
 *
 * Component-scoped — provide it on the form component, not app-wide.
 */
@Injectable()
export class ConstructionSiteFormService {
  private readonly constructionSiteRepository = inject(ConstructionSiteRepository);
  private readonly customerRepository = inject(CustomerRepository);

  /** Invoicing customer the site belongs to — `{id}` of both endpoints. */
  readonly customerId = signal<string | null>(null);
  /** Site being edited; `null` puts the form into create mode. */
  readonly constructionSiteId = signal<string | null>(null);

  readonly mode = computed<ConstructionSiteFormMode>(() =>
    this.constructionSiteId() ? 'update' : 'create'
  );

  readonly value = signal<ConstructionSiteFormValue>({
    ...EMPTY_CONSTRUCTION_SITE_FORM_VALUE,
  });
  /** Snapshot of the loaded state — the patch diffs against it. */
  private readonly original = signal<ConstructionSiteFormValue>({
    ...EMPTY_CONSTRUCTION_SITE_FORM_VALUE,
  });

  private readonly loading = signal(false);
  private readonly saving = signal(false);
  readonly isLoading = this.loading.asReadonly();
  readonly isSaving = this.saving.asReadonly();

  private readonly currentError = signal<ConstructionSiteError | null>(null);
  private readonly failed = signal(false);
  readonly error = this.currentError.asReadonly();
  readonly errorMessage = computed(() =>
    this.failed() ? constructionSiteErrorMessage(this.currentError(), this.mode()) : null
  );

  /** The backend refuses editing for this customer — keep the fields readonly, it
   *  cannot be asked in advance. */
  readonly isReadonly = computed(() => isEditNotAllowed(this.currentError()));

  /** Name 1 is treated as the one client-side requirement: which fields are truly
   *  mandatory is configured per installation and not queryable, so everything
   *  else is validated by the backend and surfaced via {@link errorMessage}. */
  readonly canSave = computed(() => {
    if (this.saving() || this.loading() || this.isReadonly()) return false;
    if (!this.value().name1.trim()) return false;
    if (this.mode() === 'create') return true;
    return hasConstructionSiteChanges(this.value(), this.original());
  });

  private loadToken = 0;

  private readonly loadExistingSite = effect(() => {
    const id = this.constructionSiteId();
    void this.load(id);
  });

  set<K extends keyof ConstructionSiteFormValue>(
    field: K,
    fieldValue: string | null | undefined
  ): void {
    this.value.update((v) => ({ ...v, [field]: fieldValue ?? '' }));
  }

  /** Loads the site's current state for prefill. The write endpoints answer with
   *  the id only, so the master data always comes from the customer GET. */
  private async load(constructionSiteId: string | null): Promise<void> {
    const token = ++this.loadToken;
    this.currentError.set(null);
    this.failed.set(false);
    if (!constructionSiteId) {
      this.loading.set(false);
      this.original.set({ ...EMPTY_CONSTRUCTION_SITE_FORM_VALUE });
      this.value.set({ ...EMPTY_CONSTRUCTION_SITE_FORM_VALUE });
      return;
    }
    this.loading.set(true);
    try {
      const customer = await this.customerRepository.getById(constructionSiteId);
      if (token !== this.loadToken) return;
      const prefill = toFormValue(customer);
      this.original.set(prefill);
      this.value.set({ ...prefill });
    } catch (e) {
      if (token !== this.loadToken) return;
      this.currentError.set(extractConstructionSiteError(e));
      this.failed.set(true);
    } finally {
      if (token === this.loadToken) this.loading.set(false);
    }
  }

  /** Creates or patches the site. Returns its `customerId` — usable wherever a
   *  customer id is expected — or `null` when the request failed. */
  async save(): Promise<string | null> {
    const customerId = this.customerId();
    if (!customerId) return null;
    const siteId = this.constructionSiteId();
    this.saving.set(true);
    this.currentError.set(null);
    this.failed.set(false);
    try {
      const response = siteId
        ? await this.constructionSiteRepository.update(
            customerId,
            siteId,
            toUpdateRequest(this.value(), this.original())
          )
        : await this.constructionSiteRepository.create(
            customerId,
            toCreateRequest(this.value())
          );
      this.original.set({ ...this.value() });
      return response?.customerId ?? null;
    } catch (e) {
      this.currentError.set(extractConstructionSiteError(e));
      this.failed.set(true);
      return null;
    } finally {
      this.saving.set(false);
    }
  }
}
